/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const AiSeoForWordpressBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      WordPress powers ~43% of the web — and somehow most of those sites are <em>invisible</em>{" "}
      on ChatGPT, Perplexity and Google's AI Overviews. The plugins they rely on (Yoast, Rank
      Math, All in One SEO) were built for 2015 Google, not 2026 answer engines. Here's what
      an <strong>AI SEO platform for WordPress</strong> actually needs to do, what to avoid,
      and how to swap your stack in under an hour.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="why-wp-struggles">Why most WordPress sites lose at AI search</h2>
    <p>Three structural reasons your WordPress site probably isn't being cited:</p>
    <ol>
      <li>
        <strong>Default robots.txt blocks AI crawlers.</strong> Plenty of "SEO" plugins still
        ship rules that disallow <code>GPTBot</code> and <code>OAI-SearchBot</code>. Audit
        yours today.
      </li>
      <li>
        <strong>Yoast/Rank Math add classic SEO schema, not AI-ready schema.</strong> They
        cover Article and BreadcrumbList — but rarely auto-generate FAQ + HowTo from your
        content (which is what ChatGPT extracts).
      </li>
      <li>
        <strong>You're publishing manually.</strong> Solo bloggers and small teams average 1–2
        posts a month. AI engines reward sites that publish 5–20+ articles a month around a
        clear topic cluster.
      </li>
    </ol>
    <p>
      An <strong>AI SEO platform for WordPress</strong> fixes all three: it researches what to
      write, drafts in your voice, injects the right schema, and publishes daily — directly to
      WP via the REST API.
    </p>

    <h2 id="must-haves">5 must-haves in an AI SEO platform for WordPress</h2>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Feature</th>
            <th className="p-3">What "good" looks like for WordPress</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["Native WP REST API publishing", "OAuth one-click connect. Posts arrive as drafts or live, with featured image, category, tags, and excerpt — no copy/paste."],
            ["AI-ready schema injection", "FAQPage, HowTo, Article and BreadcrumbList added automatically. Works alongside (not against) Yoast/Rank Math."],
            ["Brand-voice fine-tuning", "Trains on your existing posts. Output sounds like you, not generic ChatGPT prose."],
            ["Internal link suggestions", "Auto-detects relevant older posts and links them inside the new article — critical for topic cluster authority."],
            ["AI crawler & Bing readiness", "Verifies robots.txt allows GPTBot/OAI-SearchBot/CCBot. Helps you submit sitemap to Bing Webmaster Tools."],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout kind="info">
      <strong>Yoast + Rank Math don't replace this.</strong> They're great at title/meta and
      basic schema. They don't research keywords with AI engines in mind, don't write
      articles, and don't auto-inject FAQ/HowTo from your content. They're complements, not
      replacements.
    </Callout>

    <h2 id="soro-wp">The top pick for WordPress: Soro</h2>
    <p>
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Soro</a>{" "}
      ships a native WordPress integration. Connect once via OAuth, and every article it
      generates lands directly in your WP dashboard with full formatting, featured image,
      categories, tags, internal links and the FAQ + HowTo + Article schema already baked in.
      No copy/paste, no manual edits, no plugin conflicts.
    </p>

    <h3>What it actually does inside WordPress</h3>
    <ul>
      <li>Crawls your existing posts to learn topic gaps and your tone</li>
      <li>Generates buyer-intent keyword research weekly</li>
      <li>Drafts SEO + AEO-ready articles with question-style H2s and 50-word answer blocks</li>
      <li>Injects <code>FAQPage</code>, <code>HowTo</code> and <code>Article</code> JSON-LD into the post body (compatible with Yoast/Rank Math — they handle the rest of the site-wide schema)</li>
      <li>Auto-publishes daily or on a schedule, or stages as drafts for review</li>
      <li>Auto-links relevant older posts (topic cluster compounding)</li>
      <li>Works alongside any WP theme and any caching plugin</li>
    </ul>

    <h3>What Soro does <em>not</em> touch</h3>
    <p>
      Your site speed, theme, hosting, or core WordPress files. Soro is API-based — it
      doesn't install a heavy plugin or hook into the editor. That means zero risk to your
      existing WP stack.
    </p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want WordPress + AI search rankings — without touching your editor?",
        sub: "Soro publishes daily AEO-ready posts straight to your WP site via OAuth. 60-second setup.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="setup">How to set up an AI SEO platform on WordPress (under 1 hour)</h2>

    <h3>Step 1 — Audit your current WP SEO setup (10 min)</h3>
    <ol>
      <li>Open <code>yourdomain.com/robots.txt</code>. Confirm <code>GPTBot</code>, <code>OAI-SearchBot</code>, <code>Google-Extended</code>, <code>PerplexityBot</code>, <code>CCBot</code> are <strong>allowed</strong> (not disallowed).</li>
      <li>Check your XML sitemap exists (usually <code>/sitemap_index.xml</code> via Yoast).</li>
      <li>Open one of your top posts in source view. Search for <code>FAQPage</code>. If it's missing, you have an AEO gap.</li>
    </ol>

    <h3>Step 2 — Submit to Bing Webmaster Tools (5 min)</h3>
    <p>
      Connect your WordPress site at{" "}
      <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">
        bing.com/webmasters
      </a>{" "}
      and submit your sitemap. Bing is the source for ~85% of ChatGPT's citations — and most
      WP sites have never been submitted there.
    </p>

    <h3>Step 3 — Connect your AI SEO platform (5 min)</h3>
    <ol>
      <li>Sign up for Soro (or the platform of your choice).</li>
      <li>Click "Connect WordPress" → authorize OAuth.</li>
      <li>Confirm draft mode (review before publishing) or auto-publish.</li>
    </ol>

    <h3>Step 4 — Approve your first 3 article briefs (15 min)</h3>
    <p>
      The platform generates topic briefs based on your existing content gaps. Approve 3,
      tweak the angles to match what your buyers actually ask, and let it write the first
      batch overnight.
    </p>

    <h3>Step 5 — Review and ship (15 min next morning)</h3>
    <ol>
      <li>Open the drafts in WordPress.</li>
      <li>Skim for voice. Edit if anything sounds generic.</li>
      <li>Verify featured image, category, and SEO title look right (Yoast/Rank Math fields should auto-populate).</li>
      <li>Hit publish.</li>
    </ol>

    <h3>Step 6 — Set the schedule (5 min)</h3>
    <p>
      For most solo founders, 3 posts/week is the sustainable sweet spot. Marketing teams can
      push 5–10/week. AI engines reward consistent cadence and topical depth — not bursty
      publishing.
    </p>

    <h2 id="conflicts">Plugin conflicts &amp; gotchas to watch for</h2>
    <ul>
      <li><strong>Schema overlap with Yoast/Rank Math.</strong> Both plugins also inject schema. Most platforms (including Soro) detect this and gracefully add only the missing types — but if you see duplicate FAQ schema in Google's Rich Results Test, disable one source.</li>
      <li><strong>Caching plugins (WP Rocket, W3 Total Cache).</strong> Sometimes new posts don't appear immediately. Clear the cache or set the cache to revalidate on new posts.</li>
      <li><strong>Heavy page builders (Elementor, Divi).</strong> They occasionally wrap content in containers that break schema extraction. Test one published post in the Rich Results Test before scaling.</li>
      <li><strong>Multilingual plugins (WPML, Polylang).</strong> If you publish in 5+ languages, confirm your AI SEO platform supports each one natively — most don't.</li>
    </ul>

    <h2 id="roi">ROI math for WordPress sites</h2>
    <p>From real customer data on a few WP sites we run:</p>
    <ul>
      <li><strong>Month 1:</strong> 12–20 new posts indexed. First impressions in Search Console + Bing Webmaster.</li>
      <li><strong>Month 2:</strong> First ChatGPT citations on long-tail queries. 80–300 organic visits/month.</li>
      <li><strong>Month 3:</strong> Topic cluster compounding. 300–1,200 visits/month for solo founder sites; 2k–8k for established blogs.</li>
      <li><strong>Month 6:</strong> One WP affiliate site we know hit 24,000 monthly organic visits and ranked for "best [category]" with zero ad spend.</li>
    </ul>
    <p>
      At ~$100/month for the platform, the breakeven on most WP sites is <strong>under
      month 3</strong>, assuming even a modest CPC equivalent ($0.50–$2 per visit).
    </p>

    <h2 id="mistakes">3 WordPress-specific mistakes</h2>
    <ol>
      <li><strong>Letting Yoast/Rank Math block GPTBot.</strong> Their "block AI crawlers" toggle is sometimes on by default. Check it.</li>
      <li><strong>Using a heavy page builder for blog posts.</strong> Elementor + Divi inflate HTML and bury content in nested divs. Use the native Gutenberg editor for blog posts whenever possible — much cleaner for AI extraction.</li>
      <li><strong>Not submitting to Bing.</strong> Yoast submits to Google by default; Bing requires a separate step. Without it, you're invisible on ChatGPT.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="next">Your next step</h2>
    <p>
      WordPress was built before AI search existed. Your AI SEO stack has to fill the gaps —
      schema injection, daily publishing, AI-readiness. The fastest way to do that without
      touching your editor:{" "}
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>try Soro free</a>{" "}
      and see your first WordPress post ship tonight.
    </p>
    <p>
      Want the broader picture? Read{" "}
      <a href="/blog/best-ai-seo-tools-2026">our honest comparison of 7 AI SEO tools</a>{" "}
      or the solo-founder-specific guide:{" "}
      <a href="/blog/ai-seo-software-for-solo-founders">AI SEO software for solo founders</a>.
    </p>
  </>
);
